import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MenuItem } from '@/api/types';
import { NavIcon } from './NavIcon';
import { useSlideToggle } from './useSlideToggle';

/**
 * The sidebar tree, replacing the `AuthorizedMenu` view component plus the
 * behavioural half of `smartNavigation.js`.
 *
 * Class names and element nesting match the Razor output exactly — `nav-menu`,
 * `nav-title`, `nav-item`, `has-ul`, `open`, `active`, `nav-link-text`,
 * `collapse-sign` — because `styles/app/_nav.scss`, `_navdark.scss` and
 * `_navfilter.scss` all select on them and on the `ul > li > ul` shape.
 *
 * Behaviour carried over from the plugin: accordion (opening one branch closes
 * its siblings), the branch containing the current route opens itself, parent
 * links do not navigate, and the chevron flips with the open state.
 *
 * Not carried over: the "Show {count} more" collapser, which only appeared for a
 * submenu with more than 15 leaf children and which the source itself flags as
 * buggy ("has a bug when you show parent in upper tree"). Its `_navfilter.scss`
 * rules are dead until it is asked for.
 */

interface NavMenuProps {
  items: MenuItem[];
}

/** `~/foo` and `/foo/` all have to compare equal to the router's `/foo`. */
function normalizePath(url: string): string {
  const withoutTilde = url.startsWith('~') ? url.slice(1) : url;
  const path = withoutTilde.split(/[?#]/)[0];
  const rooted = path.startsWith('/') ? path : `/${path}`;
  return rooted.length > 1 && rooted.endsWith('/') ? rooted.slice(0, -1) : rooted;
}

function isExternal(url: string): boolean {
  return /^(https?:)?\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('tel:');
}

/** Ids from the root down to the item matching `pathname`, inclusive. */
function findActivePath(items: MenuItem[], pathname: string): number[] {
  for (const item of items) {
    if (item.url && !isExternal(item.url) && normalizePath(item.url) === pathname) {
      return [item.id];
    }

    const childPath = findActivePath(item.children ?? [], pathname);
    if (childPath.length > 0) return [item.id, ...childPath];
  }

  return [];
}

export function NavMenu({ items }: NavMenuProps) {
  const { pathname } = useLocation();
  const activePath = findActivePath(items, normalizePath(pathname));
  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set(activePath));

  // Navigating re-derives which branch is open, the way a full page load used to
  // ("setupActiveItems"). A branch the user opened by hand therefore closes on
  // the next navigation, which is the behaviour the Razor app had.
  const activeKey = activePath.join('/');
  useEffect(() => {
    setOpenIds(new Set(activeKey ? activeKey.split('/').map(Number) : []));
  }, [activeKey]);

  const toggle = (item: MenuItem, siblings: MenuItem[]) => {
    setOpenIds((current) => {
      const next = new Set(current);

      if (next.has(item.id)) {
        next.delete(item.id);
        return next;
      }

      // Accordion: only one branch open per level.
      for (const sibling of siblings) next.delete(sibling.id);
      next.add(item.id);
      return next;
    });
  };

  return (
    <ul id="js-nav-menu" className="nav-menu" role="menu">
      {items.length > 0 ? (
        items.map((item) => (
          <NavNode
            key={item.id}
            item={item}
            siblings={items}
            depth={1}
            openIds={openIds}
            activePath={activePath}
            onToggle={toggle}
          />
        ))
      ) : (
        <li className="nav-title">
          <span>No menu items available</span>
        </li>
      )}
    </ul>
  );
}

interface NavNodeProps {
  item: MenuItem;
  siblings: MenuItem[];
  depth: number;
  openIds: Set<number>;
  activePath: number[];
  onToggle: (item: MenuItem, siblings: MenuItem[]) => void;
}

function NavNode({ item, siblings, depth, openIds, activePath, onToggle }: NavNodeProps) {
  const children = [...(item.children ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const hasChildren = children.length > 0;
  const isOpen = openIds.has(item.id);
  const isActive = activePath.includes(item.id);

  // Called unconditionally: hooks cannot be skipped for leaf rows, and the ref
  // simply stays unattached when there is no submenu to slide.
  const submenuRef = useSlideToggle(isOpen);

  if (item.isTitle) {
    return (
      <li className="nav-title" role="separator" aria-label={item.title}>
        <span>{item.title}</span>
      </li>
    );
  }

  const badge = item.badgeText ? (
    <span className={joinClasses('badge', item.badgeCssClass)}>{item.badgeText}</span>
  ) : null;

  const contents = (
    <>
      <NavIcon icon={item.icon} />
      <span className="nav-link-text">{item.title}</span>
      {badge}
      {hasChildren ? (
        <span className="collapse-sign" aria-hidden="true">
          <i className={isOpen ? 'sa sa-chevron-up' : 'sa sa-chevron-down'} />
        </span>
      ) : null}
    </>
  );

  if (hasChildren) {
    return (
      <li
        className={joinClasses('nav-item', 'has-ul', item.cssClass, isActive && 'active', isOpen && 'open')}
        role="menuitem"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* A parent row toggles rather than navigates, as it did in the plugin. */}
        <a
          href={item.url && item.url !== '#' ? normalizePath(item.url) : '#'}
          title={item.title}
          data-filter-tags={item.dataFilterTags ?? ''}
          onClick={(event) => {
            event.preventDefault();
            onToggle(item, siblings);
          }}
        >
          {contents}
        </a>

        <ul ref={submenuRef} role="menu" data-depth={depth}>
          {children.map((child) => (
            <NavNode
              key={child.id}
              item={child}
              siblings={children}
              depth={depth + 1}
              openIds={openIds}
              activePath={activePath}
              onToggle={onToggle}
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li
      className={joinClasses(item.cssClass, isActive && 'active')}
      role="menuitem"
      aria-current={isActive ? 'page' : undefined}
    >
      <NavLink item={item} />
    </li>
  );
}

/** Leaf link: router navigation in-app, a plain anchor for anything external. */
function NavLink({ item }: { item: MenuItem }) {
  const url = item.url ?? '#';
  const shared = {
    title: item.title,
    'data-filter-tags': item.dataFilterTags ?? '',
  };

  const contents = (
    <>
      <NavIcon icon={item.icon} />
      <span className="nav-link-text">{item.title}</span>
      {item.badgeText ? (
        <span className={joinClasses('badge', item.badgeCssClass)}>{item.badgeText}</span>
      ) : null}
    </>
  );

  if (url === '#' || isExternal(url)) {
    return (
      <a
        href={url}
        target={item.target ?? undefined}
        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        {...shared}
      >
        {contents}
      </a>
    );
  }

  return (
    <Link to={normalizePath(url)} target={item.target ?? undefined} {...shared}>
      {contents}
    </Link>
  );
}

function joinClasses(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}
